import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    date: '',
    numberOfChildren: '1',
    specialRequirements: '',
    newsletter: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('eventType', formData.eventType);
      formBody.append('date', formData.date);
      formBody.append('numberOfChildren', formData.numberOfChildren);
      formBody.append('specialRequirements', formData.specialRequirements);
      formBody.append('newsletter', formData.newsletter ? 'Yes' : 'No');

      const response = await fetch('https://readdy.ai/api/form/d6ndpfs6fojmejgajq20', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventType: '',
          date: '',
          numberOfChildren: '1',
          specialRequirements: '',
          newsletter: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  return (
    <section id="contact" className="py-24 bg-zinc-900">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block mb-6 px-6 py-2 bg-red-600 rounded-full">
            <span className="text-white text-sm font-semibold uppercase tracking-wide">Get In Touch</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Book Your Magical Journey
          </h2>
          <p className="text-xl text-white/60">
            We'll respond within 24 hours
          </p>
        </div>

        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-12">
          <div className="md:col-span-2">
            <form 
              id="contact-form" 
              data-readdy-form 
              onSubmit={handleSubmit} 
              className="bg-black rounded-2xl p-8 md:p-12 border border-white/10"
            >
              <div className="space-y-7">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name *"
                    required
                    className="w-full bg-transparent border-b-2 border-white/20 focus:border-red-600 text-white placeholder-white/40 py-3 px-0 outline-none transition-colors text-base"
                  />
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address *"
                    required
                    className="w-full bg-transparent border-b-2 border-white/20 focus:border-red-600 text-white placeholder-white/40 py-3 px-0 outline-none transition-colors text-base"
                  />
                </div>

                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone Number *"
                    required
                    className="w-full bg-transparent border-b-2 border-white/20 focus:border-red-600 text-white placeholder-white/40 py-3 px-0 outline-none transition-colors text-base"
                  />
                </div>

                <div>
                  <select
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b-2 border-white/20 focus:border-red-600 text-white py-3 px-0 outline-none transition-colors cursor-pointer text-base"
                  >
                    <option value="" className="bg-black">Select Event Type *</option>
                    <option value="Birthday Party" className="bg-black">Birthday Party</option>
                    <option value="Holiday Event" className="bg-black">Holiday Event</option>
                    <option value="Special Needs Group" className="bg-black">Special Needs Group</option>
                    <option value="Charity Event" className="bg-black">Charity Event</option>
                    <option value="Corporate Event" className="bg-black">Corporate Event</option>
                    <option value="Other" className="bg-black">Other</option>
                  </select>
                </div>

                <div>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b-2 border-white/20 focus:border-red-600 text-white py-3 px-0 outline-none transition-colors cursor-pointer text-base"
                  />
                </div>

                <div>
                  <input
                    type="number"
                    name="numberOfChildren"
                    value={formData.numberOfChildren}
                    onChange={handleChange}
                    min="1"
                    placeholder="Number of Children *"
                    required
                    className="w-full bg-transparent border-b-2 border-white/20 focus:border-red-600 text-white placeholder-white/40 py-3 px-0 outline-none transition-colors text-base"
                  />
                </div>

                <div>
                  <textarea
                    name="specialRequirements"
                    value={formData.specialRequirements}
                    onChange={handleChange}
                    placeholder="Please share any accessibility needs or special requests"
                    maxLength={500}
                    rows={5}
                    className="w-full bg-transparent border-b-2 border-white/20 focus:border-red-600 text-white placeholder-white/40 py-3 px-0 outline-none transition-colors resize-none text-base"
                  ></textarea>
                  <div className="text-right text-white/40 text-sm mt-2">
                    {formData.specialRequirements.length}/500
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="newsletter"
                    id="newsletter"
                    checked={formData.newsletter}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 accent-red-600 cursor-pointer"
                  />
                  <label htmlFor="newsletter" className="text-white/80 cursor-pointer text-base">
                    Keep me updated on special events and holiday rides
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="whitespace-nowrap w-full md:w-auto px-12 py-4 bg-red-600 text-white text-lg font-semibold rounded-lg hover:bg-red-700 transition-all duration-300 hover:scale-105 cursor-pointer flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-600/20 border border-green-600 text-green-400 px-6 py-4 rounded-lg">
                    Thank you! We'll get back to you within 24 hours.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-600/20 border border-red-600 text-red-400 px-6 py-4 rounded-lg">
                    Something went wrong. Please try again or contact us directly.
                  </div>
                )}
              </div>
            </form>
          </div>

          <div className="space-y-8">
            <div className="bg-black rounded-2xl p-8 border border-white/10">
              <h3 className="text-2xl font-bold text-white mb-8">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded-full flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Address</h4>
                    <p className="text-white/70 text-sm leading-relaxed">
                      Cornwall, Ontario
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded-full flex-shrink-0">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Phone</h4>
                    <a href="tel:+15551234567" className="text-white/70 hover:text-red-500 transition-colors text-sm cursor-pointer">
                      (613)-861-9099
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded-full flex-shrink-0">
                    <i className="ri-mail-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Email</h4>
                    <a href="mailto:info@jmstrainrides.com" className="text-white/70 hover:text-red-500 transition-colors text-sm cursor-pointer">
                      info@jmstrainrides.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/10">
                <h4 className="text-white font-semibold mb-4">Follow Us</h4>
                <div className="flex gap-3">
                  <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-white/30 text-white hover:border-red-600 hover:bg-red-600 transition-all cursor-pointer">
                    <i className="ri-facebook-fill"></i>
                  </a>
                  <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-white/30 text-white hover:border-red-600 hover:bg-red-600 transition-all cursor-pointer">
                    <i className="ri-instagram-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}